import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { ArrowRightLeft, ChevronDown } from 'lucide-react';
import { BsPersonCircle as ProfilePicture } from 'react-icons/bs';
import { MdOutlineClose as CloseIcon} from "react-icons/md";
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import OptionsDropDown from '@CommonFunctionalDisplay/OptionsDropDown';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useNavigateWithRedirect } from '@Hooks/useNavigateWithRedirect';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './TopPanel.scss';


export default function TopPanel(props) {

    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [openNav, setOpenNav] = useState(null);
    new UserAuthService();
    const is0To768 = useMediaQuery({ maxWidth: 768 });
    const navigate = useNavigateWithRedirect();
    const { activeUser, userType, setUserType, activeUserAccessList } = useReduxActiveUser();
    const { setCurrentProject } = useReduxProject();
    const className = 'topPanel';

    const isLoggedIn = !!activeUser && Object.keys(activeUser).length > 0 && activeUser?.id > 0;
    const isReporter = isLoggedIn && userType === 'reporter';
    // Show nav when not logged in OR when logged in as reporter
    const showNav = !isLoggedIn || isReporter;

    // ─── Nav data ─────────────────────────────────────────────────────

    const BASE_NAV = [
        {
            id: 'platform',
            label: 'Platform',
            type: 'dropdown',
            items: [
                { label: 'Why KaizIQ',    desc: 'See what sets us apart from the rest',    onClick: () => navigate('/why-kaiziq') },
                { label: 'Documentation', desc: 'Guides, APIs, and technical references',   onClick: () => window.open('https://docs.kaiziq.io', '_blank') },
                { label: 'Blogs',         desc: 'Tips, stories, and product updates',       onClick: () => navigate('/blogs') },
                { label: 'Changelog',     desc: "What's new in every release",              onClick: () => navigate('/changelog') },
            ],
        },
        {
            id: 'solutions',
            label: 'Solutions',
            type: 'mega',
            columns: [
                {
                    heading: 'By Company Size',
                    links: [
                        { label: 'Enterprise',           onClick: () => navigate('/solutions/enterprise') },
                        { label: 'Small & Medium Teams', onClick: () => navigate('/solutions/smb') },
                        { label: 'Startups',             onClick: () => navigate('/solutions/startups') },
                        { label: 'Nonprofits',           onClick: () => navigate('/solutions/nonprofits') },
                    ],
                },
                {
                    heading: 'By Use Case',
                    links: [
                        { label: 'IT Help Desk',          onClick: () => navigate('/solutions/it-help-desk') },
                        { label: 'HR Operations',         onClick: () => navigate('/solutions/hr-operations') },
                        { label: 'Facilities Management', onClick: () => navigate('/solutions/facilities') },
                        { label: 'Customer Complaints',   onClick: () => navigate('/solutions/customer-complaints') },
                        { label: 'Internal Feedback',     onClick: () => navigate('/solutions/internal-feedback') },
                    ],
                },
                {
                    heading: 'By Industry',
                    links: [
                        { label: 'Education',           onClick: () => navigate('/solutions/education') },
                        { label: 'Healthcare',          onClick: () => navigate('/solutions/healthcare') },
                        { label: 'Retail & E-commerce', onClick: () => navigate('/solutions/retail') },
                        { label: 'Financial Services',  onClick: () => navigate('/solutions/financial-services') },
                        { label: 'Manufacturing',       onClick: () => navigate('/solutions/manufacturing') },
                    ],
                },
            ],
        },
        {
            id: 'resources',
            label: 'Resources',
            type: 'dropdown',
            items: [
                { label: 'Documentation',    desc: 'Comprehensive guides and references',      onClick: () => window.open('https://docs.kaiziq.io', '_blank') },
                { label: 'Customer Support', desc: 'Reach our support team anytime',           onClick: () => navigate('/support') },
                { label: 'Case Studies',     desc: 'How teams win with KaizIQ Resolve',        onClick: () => navigate('/case-studies') },
                { label: 'Ebooks',           desc: 'Deep dives into support excellence',       onClick: () => navigate('/ebooks') },
            ],
        },
        {
            id: 'pricing',
            label: 'Pricing',
            type: 'link',
            onClick: () => navigate('/pricing'),
        },
    ];

    const REPORTER_EXTRA = [
        {
            id: 'track',
            label: 'Track Ticket',
            type: 'link',
            onClick: () => navigate('/home/track'),
        },
        {
            id: 'report',
            label: 'Report Ticket',
            type: 'link',
            onClick: () => navigate('/home/form/search'),
        },
    ];

    const navItems = isReporter ? [...BASE_NAV, ...REPORTER_EXTRA] : BASE_NAV;

    // ─── Handlers ─────────────────────────────────────────────────────

    const handleLogoClick = () => navigate('/');

    const handleLoginClick = () => navigate('/login');

    const handleMobileMenu = () => {
        if (isLoggedIn) {
            setShowMobileMenu(prev => !prev);
        } else {
            handleLoginClick();
        }
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const logOut = () => navigate('/logout');

    const handleProfileClick = () => navigate('/home/userManagement');

    const handleAdminLoginClick = () => {
        setUserType('admin');
        navigate('/login?loginType=admin');
    };

    const handleUserType = () => {
        let projects = activeUserAccessList?.projects;
        let resolveProject = projects?.find((project) => project.visibility === 'resolve');
        if (!!resolveProject) {
            setCurrentProject(resolveProject);
        }
        if (userType === 'reporter') {
            setUserType('admin');
            navigate('/dashboard');
        } else {
            setUserType('reporter');
            navigate('/home');
        }
    };

    // ─── Render ───────────────────────────────────────────────────────

    return (
        <div className={className + ' flex-row-flex-end-space-between width-wfa ' + (userType === 'reporter' ? 'topPanel_report' : '')}>

            {/* Logo */}
            <div className={className + '__left flex-row-center-1vw-gap'}>
                <KaizIQText className={className + '__left__logo'} onClick={handleLogoClick} />
            </div>

            {/* Desktop navigation — absolutely centred */}
            {showNav && !is0To768 && (
                <nav className={className + '__nav'}>
                    {navItems.map((item) => (
                        <div
                            key={item.id}
                            className={className + '__nav__item'}
                            onMouseEnter={() => item.type !== 'link' ? setOpenNav(item.id) : undefined}
                            onMouseLeave={() => setOpenNav(null)}
                        >
                            {/* Trigger button */}
                            <button
                                className={
                                    className + '__nav__trigger' +
                                    (openNav === item.id ? ' ' + className + '__nav__trigger--active' : '') +
                                    (item.type === 'link' ? ' ' + className + '__nav__trigger--link' : '')
                                }
                                onClick={item.onClick}
                            >
                                {item.label}
                                {item.type !== 'link' && (
                                    <ChevronDown
                                        size={13}
                                        className={
                                            className + '__nav__chevron' +
                                            (openNav === item.id ? ' ' + className + '__nav__chevron--open' : '')
                                        }
                                    />
                                )}
                            </button>

                            {/* Simple dropdown */}
                            {item.type === 'dropdown' && (
                                <div className={
                                    className + '__nav__dropdown' +
                                    (openNav === item.id ? ' ' + className + '__nav__dropdown--open' : '')
                                }>
                                    {item.items.map((link, i) => (
                                        <div
                                            key={i}
                                            className={className + '__nav__dropdown__item'}
                                            onClick={link.onClick}
                                        >
                                            <span className={className + '__nav__dropdown__label'}>{link.label}</span>
                                            <span className={className + '__nav__dropdown__desc'}>{link.desc}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Mega menu */}
                            {item.type === 'mega' && (
                                <div className={
                                    className + '__nav__mega' +
                                    (openNav === item.id ? ' ' + className + '__nav__mega--open' : '')
                                }>
                                    {item.columns.map((col, ci) => (
                                        <div key={ci} className={className + '__nav__mega__col'}>
                                            <h5 className={className + '__nav__mega__col__heading'}>
                                                {col.heading}
                                            </h5>
                                            {col.links.map((link, li) => (
                                                <div key={li} className={className + '__nav__mega__col__link'} onClick={link.onClick}>
                                                    {link.label}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>
            )}

            {/* Mobile profile icon */}
            {is0To768 && (
                <div className={className + '__right__logo'}>
                    {!!activeUser?.photo && activeUser?.photo !== 'null' && activeUser?.photo !== '' ? (
                        <img
                            src={URL.createObjectURL(dataURLtoFile(activeUser?.photo))}
                            className={className + '__right__logo__photo'}
                            alt='profile'
                            onClick={handleMobileMenu}
                        />
                    ) : (
                        <ProfilePicture
                            className={className + '__right__logo__icon grey-text standard-profile-pic'}
                            onClick={handleMobileMenu}
                        />
                    )}
                </div>
            )}

            {/* Right action area */}
            <div className={className + '__right' + (showMobileMenu ? '__open' : '__close') + ' flex-row-center-1vw-gap'}>
                {is0To768 && (
                    <CloseIcon
                        className={className + '__right__close__menuIcon'}
                        onClick={handleMobileMenu}
                    />
                )}

                {isLoggedIn ? (
                    !is0To768 ? (
                        <>
                            <div className={className + '__right__switchRole'} onClick={handleUserType}>
                                <ArrowRightLeft className={className + '__right__switchRole__icon'} />
                                <span className={className + '__right__switchRole__label'}>
                                    Switch to {userType === 'reporter' ? 'Admin' : 'Reporter'}
                                </span>
                            </div>
                            <OptionsDropDown />
                        </>
                    ) : (
                        <>
                            <div className={className + '__right__b3'}>
                                <BorderWhiteButtonV2 text={'Profile'} onClick={handleProfileClick} />
                            </div>
                            <div className={className + '__right__b3'}>
                                <BorderWhiteButtonV2
                                    text={'Switch As ' + (userType === 'reporter' ? 'Admin' : 'Reporter')}
                                    onClick={handleUserType}
                                />
                            </div>
                            <div className={className + '__right__b4'}>
                                <BorderWhiteButtonV2 text={'Logout'} onClick={logOut} />
                            </div>
                        </>
                    )
                ) : (
                    <>
                        <div className={className + '__right__adminLogin'} onClick={handleAdminLoginClick}>
                            <span>Admin Login</span>
                        </div>
                        <div className={className + '__right__b3'}>
                            <BorderBlueButtonV2 text={'Login'} onClick={handleLoginClick} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
